import { useField } from 'formik';
import { rgba } from 'polished';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Label } from '@treams/treams-ui';

import Tooltip from '../../../../packages/kakapo-ui-kit/components/tooltips/Tooltip';

const Emoji = styled.div<{ selected: boolean }>`
	width: 48px;
	height: 48px;
	border-radius: 5px;
	font-size: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: 0.3s ease-in-out all;

	background-color: ${(props) => (props.selected ? props.theme.colors.primary : 'white')};

	&:hover {
		box-shadow: 0px 4px 8px ${(props) => rgba(props.theme.colors.primary, 0.25)};
		transform: scale(1.2, 1.2);

		&:active {
			transform: scale(0.9, 0.9);
		}
	}
`;

function Choice(
	props: PropsWithChildren<{ selected: boolean; onClick: () => void; tooltip?: string }>,
) {
	const [hover, setHover] = useState(false);

	return (
		<Box
			style={{ position: 'relative' }}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			{hover && <Tooltip tiny>{props.tooltip}</Tooltip>}
			<Emoji selected={props.selected} onClick={props.onClick}>
				{props.children}
			</Emoji>
		</Box>
	);
}

export default function Sentiment(props: {
	name: string;
	item: graphql.EvaluationSentimentItem;
	readonly?: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [field, , { setValue }] = useField<number>(props.name + '.sentiment');
	const value = field.value ?? -1;

	return (
		<>
			<Box style={{ maxWidth: 600 }}>
				<Label>{localize(props.item.title).text}</Label>
			</Box>
			<Flex alignItems="center" mt={2}>
				<Box mr={2}>
					<Choice
						selected={value === 0}
						onClick={() => props.readonly || setValue(0)}
						tooltip={t('evaluation.submission.sentiment.sad')}
					>
						😥
					</Choice>
				</Box>
				<Box mr={2}>
					<Choice
						selected={value === 1}
						onClick={() => props.readonly || setValue(1)}
						tooltip={t('evaluation.submission.sentiment.disappointed')}
					>
						😕
					</Choice>
				</Box>
				<Box mr={2}>
					<Choice
						selected={value === 2}
						onClick={() => props.readonly || setValue(2)}
						tooltip={t('evaluation.submission.sentiment.neutral')}
					>
						🤔
					</Choice>
				</Box>
				<Box mr={2}>
					<Choice
						selected={value === 3}
						onClick={() => props.readonly || setValue(3)}
						tooltip={t('evaluation.submission.sentiment.happy')}
					>
						🙂
					</Choice>
				</Box>
				<Box mr={2}>
					<Choice
						selected={value === 4}
						onClick={() => props.readonly || setValue(4)}
						tooltip={t('evaluation.submission.sentiment.enthusiastic')}
					>
						😃
					</Choice>
				</Box>
			</Flex>
		</>
	);
}
